type JsonPrimitive = null | boolean | number | string;
export type JsonValue =
  | JsonPrimitive
  | JsonValue[]
  | { [key: string]: JsonValue };

export const isJsonValue = (value: unknown): value is JsonValue => {
  if (value === null) return true;
  switch (typeof value) {
    case "string":
    case "boolean":
      return true;
    case "number":
      return Number.isFinite(value);
    case "object": {
      if (Array.isArray(value)) {
        return value.every(isJsonValue);
      }
      return Object.values(value as Record<string, unknown>).every(isJsonValue);
    }
    default:
      return false;
  }
};

const cloneJsonValue = (value: JsonValue): JsonValue => {
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value.map((item) => cloneJsonValue(item));
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, JsonValue>);
    return entries.reduce<Record<string, JsonValue>>((acc, [key, item]) => {
      acc[key] = cloneJsonValue(item);
      return acc;
    }, {});
  }
  return value;
};

export const cloneOptionalJsonValue = (
  value: JsonValue | null | undefined,
): JsonValue | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return cloneJsonValue(value);
};

export const coerceOptionalJsonValue = (
  value: unknown,
): JsonValue | null | undefined => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return coerceJsonValue(value);
};

const coerceJsonValue = (value: unknown): JsonValue | undefined => {
  if (value === null) {
    return null;
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (Array.isArray(value)) {
    const result: JsonValue[] = [];
    for (const item of value) {
      const coerced = coerceOptionalJsonValue(item);
      if (coerced !== undefined) {
        result.push(coerced as JsonValue);
      }
    }
    return result;
  }
  if (typeof value === "object") {
    const result: Record<string, JsonValue> = {};
    for (const [key, item] of Object.entries(
      value as Record<string, unknown>,
    )) {
      const coerced = coerceOptionalJsonValue(item);
      if (coerced !== undefined) {
        result[key] = coerced as JsonValue;
      }
    }
    return result;
  }
  return undefined;
};
